import React, { useCallback } from 'react';
import { Modal, Text, TextInput, Button, PhoneInput, SelectInput } from '@/ui-kits';
import { useFormik } from 'formik';
import { AddHospitalPocRequestPayload } from '@/interface/api-interface';
import { AddHospitalPocFormValidation } from '@/utils/schema';
import { statusOptions } from '@/constants';
import { createFormattedBlurHandler } from '@/utils/common_functions';
import { hospitalAdminPocListLoadingSelector } from '@/services/redux/selectors/hospitalAdminSelector';
import { useSelector } from 'react-redux';
interface AddHospitalPocModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: AddHospitalPocRequestPayload) => void;
} 

const AddHospitalPocFormInitialValues: AddHospitalPocRequestPayload = {
  name: '',
  email: '',
  phone: '',
  hospital_name: '',
  status: true,
}
export const AddHospitalPocModal: React.FC<AddHospitalPocModalProps> = (props) => {
  const { open, onClose, onSave } = props;

  // selectors
  const hospitalAdminPocListLoading = useSelector(hospitalAdminPocListLoadingSelector);
  
  // hooks
  const { values, errors, touched, handleBlur, handleSubmit, handleChange, setFieldValue, isValid, dirty } = useFormik<AddHospitalPocRequestPayload>({
    initialValues: AddHospitalPocFormInitialValues,
    validationSchema: AddHospitalPocFormValidation,
    onSubmit: onSave,
  });

  // Create formatted blur handlers for name fields to capitalize first letters
  const handleNameBlur = createFormattedBlurHandler(handleBlur, setFieldValue, 'name');
  const handleHospitalNameBlur = createFormattedBlurHandler(handleBlur, setFieldValue, 'hospital_name');

  const closeModal = useCallback(() => {
    if (!dirty) {
      onClose();
    }
  }, [dirty, onClose]);

  return (
    <Modal open={open} onClose={closeModal} className="relative backdrop:bg-[#0000007e] bg-white py-4 sm:py-6 px-4 sm:px-6 md:px-8 rounded-xl gap-4 w-[90vw] max-w-[500px] shadow-lg">
      <Text variant="heading" className="text-ui_blue">
        Add Hospital-Level Admin
      </Text>

      <div className="flex flex-col gap-2 sm:gap-4 my-5">
        {/* Admin Name */}
        <TextInput
          name="name"
          label="Admin Name"
          value={values.name}
          onChangeText={handleChange('name')}
          onBlur={handleNameBlur as any}
          error={errors.name}
          touched={touched.name}
          required
          preventLeadingSpace
          preventTrailingSpace
          placeholder="Enter Admin Name"
          wrapperClassName="border w-full"
        />

        {/* Email Address */}
        <TextInput
          name="email"
          label="Email Address"
          value={values.email}
          onChangeText={handleChange('email')}
          onBlur={handleBlur('email')}
          error={errors.email}
          touched={touched.email}
          required
          placeholder="Enter Email Address"
          wrapperClassName="border w-full"
        />
        
        {/* Phone Number */}
        <PhoneInput
          name="phone"
          label="Phone Number"
          value={values.phone}
          onChange={(phone) => setFieldValue('phone', phone)}
          onBlur={handleBlur('phone')}
          error={errors.phone}
          touched={touched.phone}
          required
          placeholder="Enter Phone Number"
          wrapperClassName="border w-full"
        />
        
        {/* Hospital Name */}
        <TextInput
          name="hospital_name"
          label="Hospital Name"
          value={values.hospital_name}
          onChangeText={handleChange('hospital_name')}
          onBlur={handleHospitalNameBlur as any}
          error={errors.hospital_name}
          touched={touched.hospital_name}
          required
          max={150}
          placeholder="Select Hospital Name"
          wrapperClassName="border w-full"
        />

        <SelectInput
          name="status"
          label="Status"
          value={values.status ? 1 : 2}
          onChange={item => setFieldValue('status', item.id === 1)}
          onBlur={handleBlur('status')}
          error={errors.status}
          touched={touched.status}
          required
          placeholder="Select Status"
          wrapperClassName="border w-full"
          options={statusOptions}
        />


        {/* Action Buttons */}
      </div>
        <div className="flex gap-4 items-center justify-center">  
          <Button className="w-full max-w-[130px] py-2" variant="secondary" title="Cancel" onClick={onClose} />
          <Button className="w-full max-w-[130px] py-2" isLoading={hospitalAdminPocListLoading} variant="primary" title="Save" onClick={() => handleSubmit()} disabled={!isValid || !dirty} />
        </div>
    </Modal>
  );
};
